import { useMemo } from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import DispatchCard from "./DispatchCard";

export default function DispatchBoard({
  items,
  filters,
  filterOptions,
  onFilterChange,
  onOpen,
  onDragStart,
}) {
  const orderedItems = useMemo(() => {
    const priorityOrder = { spoed: 0, hoog: 1, normaal: 2, laag: 3 };
    const statusOrder = { concept: 0, gereed_voor_planning: 1, gepland: 2, in_uitvoering: 3 };

    return [...items].sort((a, b) => {
      const blockedDiff = Number(Boolean(b?.isBlocked)) - Number(Boolean(a?.isBlocked));
      if (blockedDiff !== 0) return blockedDiff;

      const priorityDiff =
        (priorityOrder[a?.priorityKey] ?? 99) - (priorityOrder[b?.priorityKey] ?? 99);
      if (priorityDiff !== 0) return priorityDiff;

      const statusDiff = (statusOrder[a?.statusKey] ?? 99) - (statusOrder[b?.statusKey] ?? 99);
      if (statusDiff !== 0) return statusDiff;

      return String(a?.werkbonNummer || "").localeCompare(String(b?.werkbonNummer || ""), "nl-NL");
    });
  }, [items]);

  return (
    <section className="flex h-full min-h-[640px] flex-col rounded-[26px] border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-4 py-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
              Werkvoorraad
            </p>
            <h2 className="mt-1 text-lg font-semibold text-slate-950">Open werk en intake</h2>
          </div>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700">
            {items.length}
          </span>
        </div>

        <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3">
          <div className="relative">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={filters.query}
              onChange={(e) => onFilterChange("query", e.target.value)}
              placeholder="Zoek werkbon, klant, locatie..."
              className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm outline-none transition focus:border-slate-400"
            />
          </div>

          <div className="mt-3 grid gap-2">
            <FilterRow
              label="Status"
              value={filters.status}
              onChange={(value) => onFilterChange("status", value)}
              options={filterOptions.statuses}
            />
            <FilterRow
              label="Project"
              value={filters.project}
              onChange={(value) => onFilterChange("project", value)}
              options={filterOptions.projects}
            />
            <FilterRow
              label="Klant"
              value={filters.client}
              onChange={(value) => onFilterChange("client", value)}
              options={filterOptions.clients}
            />
            <FilterRow
              label="Discipline"
              value={filters.discipline}
              onChange={(value) => onFilterChange("discipline", value)}
              options={filterOptions.disciplines}
            />
            <FilterRow
              label="Prioriteit"
              value={filters.priority}
              onChange={(value) => onFilterChange("priority", value)}
              options={filterOptions.priorities}
            />
          </div>
        </div>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
        {orderedItems.length === 0 ? (
          <EmptyDispatch />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
            {orderedItems.map((item) => (
              <DispatchCard
                key={item.id}
                item={item}
                onOpen={onOpen}
                onDragStart={onDragStart}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function FilterRow({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
        <SlidersHorizontal size={12} />
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-slate-400"
      >
        <option value="all">Alles</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function EmptyDispatch() {
  return (
    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
      Geen werkbonnen in de huidige werkvoorraadfilters.
    </div>
  );
}
